import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  calculatorOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  copyOutline,
  informationCircleOutline,
  starOutline,
} from 'ionicons/icons';
import { useState } from 'react';
import {
  COMMERCIAL_ADDONS,
  COMMERCIAL_FEATURES,
  COMMERCIAL_PACKAGES,
  PACKAGE_INCLUDED_ADDONS,
  annualFullPriceFromMonthly,
  annualUpfrontPriceFromMonthly,
  calculateCommercialQuote,
  calculateConsultantCommission,
  calculateConsultantScenario,
  formatCommercialQuoteSummary,
  parseConsultantClientCount,
  parseConsultantMonthlyPrice,
  type CommercialAddonId,
  type CommercialBillingCycle,
  type CommercialPackage,
  type CommercialPackageId,
} from '../../lib/commercial/pricing.js';
import './ConsultantPricingPage.css';

const euro = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const percent = new Intl.NumberFormat('it-IT', { style: 'percent', maximumFractionDigits: 2 });

function toBoundedInteger(value: unknown, minimum: number, maximum: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return minimum;
  return Math.min(maximum, Math.max(minimum, Math.floor(parsed)));
}

export function ConsultantPricingPage(): JSX.Element {
  const [packageId, setPackageId] = useState<CommercialPackageId>('complete');
  const [billingCycle, setBillingCycle] = useState<CommercialBillingCycle>('monthly');
  const [devices, setDevices] = useState(3);
  const [selectedAddonIds, setSelectedAddonIds] = useState<CommercialAddonId[]>([]);
  const [extraDocumentPacks, setExtraDocumentPacks] = useState(0);
  const [connectedProbes, setConnectedProbes] = useState(0);
  const [projectionPackageId, setProjectionPackageId] = useState<CommercialPackageId>('complete');
  const [projectionPriceInput, setProjectionPriceInput] = useState('130');
  const [projectionClientsInput, setProjectionClientsInput] = useState('10');
  const [projectionBillingCycle, setProjectionBillingCycle] = useState<CommercialBillingCycle>('monthly');
  const [toast, setToast] = useState<string | null>(null);

  const input = {
    packageId,
    billingCycle,
    devices,
    selectedAddonIds,
    extraDocumentPacks,
    connectedProbes,
  };
  const quote = calculateCommercialQuote(input);
  const effectiveAddonIds = new Set(quote.effectiveAddonIds);
  const includedAddonIds = new Set(PACKAGE_INCLUDED_ADDONS[packageId]);
  const enterpriseSelected = quote.packageDefinition.priceVisibility === 'quote';
  const projectionMonthlyPrice = parseConsultantMonthlyPrice(projectionPriceInput);
  const projectionClients = parseConsultantClientCount(projectionClientsInput);
  const projectionCommission = projectionMonthlyPrice === null ? null : calculateConsultantCommission(projectionMonthlyPrice);
  const singleMonthly = projectionMonthlyPrice === null ? null : calculateConsultantScenario(projectionMonthlyPrice, 1, 'monthly');
  const singleAnnual = projectionMonthlyPrice === null ? null : calculateConsultantScenario(projectionMonthlyPrice, 1, 'annual');
  const projectionScenario = projectionMonthlyPrice === null || projectionClients === null
    ? null
    : calculateConsultantScenario(projectionMonthlyPrice, projectionClients, projectionBillingCycle);
  const projectionPackage = COMMERCIAL_PACKAGES.find((item) => item.id === projectionPackageId)
    ?? COMMERCIAL_PACKAGES[1];

  function toggleAddon(addonId: CommercialAddonId, checked: boolean): void {
    setSelectedAddonIds((current) => {
      if (checked) return current.includes(addonId) ? current : [...current, addonId];
      return current.filter((id) => id !== addonId);
    });
  }

  async function copySummary(): Promise<void> {
    try {
      await navigator.clipboard.writeText(formatCommercialQuoteSummary(input));
      setToast('Riepilogo copiato');
    } catch {
      setToast('Copia non disponibile su questo dispositivo');
    }
  }

  function renderPackageCard(item: CommercialPackage): JSX.Element {
    const selected = packageId === item.id;
    const enterprise = item.priceVisibility === 'quote';
    const annualFullPrice = item.monthlyPrice === null ? null : annualFullPriceFromMonthly(item.monthlyPrice);
    const annualUpfrontPrice = item.monthlyPrice === null ? null : annualUpfrontPriceFromMonthly(item.monthlyPrice);
    const annualSavings = annualFullPrice === null || annualUpfrontPrice === null ? null : annualFullPrice - annualUpfrontPrice;
    return (
      <button
        type="button"
        key={item.id}
        className={`te-pricing-package${selected ? ' te-pricing-package--selected' : ''}${item.recommended ? ' te-pricing-package--recommended' : ''}${enterprise ? ' te-pricing-package--enterprise' : ''}`}
        aria-pressed={selected}
        onClick={() => setPackageId(item.id)}
      >
        {item.recommended && (
          <span className="te-pricing-package__badge">
            <IonIcon icon={starOutline} aria-hidden="true" />
            Consigliato
          </span>
        )}
        <span className="te-pricing-package__topline">
          <strong>{item.name}</strong>
          <span>{item.monthlyPrice === null ? 'Su proposta' : `${euro.format(item.monthlyPrice)}/mese`}</span>
        </span>
        {annualFullPrice !== null && annualUpfrontPrice !== null && annualSavings !== null ? (
          <span className="te-pricing-package__annual-prices">
            <span className="te-pricing-package__price-row">
              <span>Costo annuo a prezzo pieno</span><strong>{euro.format(annualFullPrice)}</strong>
            </span>
            <span className="te-pricing-package__price-row te-pricing-package__price-row--discounted">
              <span>Pagamento annuale anticipato<small>Una tantum · sconto 10% · risparmio {euro.format(annualSavings)}</small></span>
              <strong>{euro.format(annualUpfrontPrice)}</strong>
            </span>
          </span>
        ) : (
          <span className="te-pricing-package__annual-prices">Canone e setup da definire in base a sedi, moduli e necessità. Pagamento annuale anticipato: −10% sul canone concordato.</span>
        )}
        <span className="te-pricing-package__summary">{item.summary}</span>
        <ul className="te-pricing-package__features">
          {COMMERCIAL_FEATURES.map((feature) => {
            const available = feature.availability[item.id];
            return (
              <li key={feature.id} className={available ? 'is-included' : 'is-excluded'}>
                <IonIcon icon={available ? checkmarkCircleOutline : closeCircleOutline} aria-hidden="true" />
                <span>{feature.label}</span>
              </li>
            );
          })}
        </ul>
        <span className="te-pricing-package__selection">
          <IonIcon icon={selected ? checkmarkCircleOutline : calculatorOutline} aria-hidden="true" />
          {selected ? 'Pacchetto selezionato' : 'Seleziona pacchetto'}
        </span>
      </button>
    );
  }

  const currentCommission = quote.monthlyTotal === null
    ? null
    : calculateConsultantScenario(quote.monthlyTotal, 1, billingCycle);

  return (
    <IonPage className="te-pricing-page">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Configura offerta</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="te-page">
        <main className="te-page-container te-pricing-shell">
          <header className="te-pricing-heading">
            <span className="te-pricing-heading__icon" aria-hidden="true">
              <IonIcon icon={calculatorOutline} />
            </span>
            <div>
              <h1 className="te-page-heading">Configuratore commerciale</h1>
              <p className="te-page-subtitle">Canoni per sede · IVA esclusa</p>
            </div>
          </header>

          <section className="te-pricing-total" aria-live="polite" aria-label="Riepilogo del canone">
            <div>
              <span className="te-pricing-eyebrow">{billingCycle === 'annual' ? 'Pagamento annuale anticipato' : 'Canone risultante'}</span>
              <strong className="te-pricing-total__value">
                {enterpriseSelected || quote.billingTotal === null ? 'Su proposta' : euro.format(quote.billingTotal)}
              </strong>
              {!enterpriseSelected && quote.billingTotal !== null && (
                <span className="te-pricing-total__period">{billingCycle === 'annual' ? 'una tantum + IVA' : '/ mese + IVA'}</span>
              )}
            </div>
            <IonButton fill="outline" onClick={() => { void copySummary(); }}>
              <IonIcon slot="start" icon={copyOutline} />
              Copia riepilogo cliente
            </IonButton>
            <div className="te-pricing-total__meta">
              <span>{quote.packageDefinition.name} · setup {quote.packageDefinition.setupFee === null ? 'da definire' : `da ${euro.format(quote.packageDefinition.setupFee)}`}</span>
              <span>{devices <= 3 ? `${devices} dispositiv${devices === 1 ? 'o' : 'i'} inclus${devices === 1 ? 'o' : 'i'}` : `${devices} dispositivi · maggiorazione ${quote.deviceSurcharge === null ? 'da definire' : euro.format(quote.deviceSurcharge)}`}</span>
              {!enterpriseSelected && quote.annualFullPrice !== null && (
                <span>Costo annuo a prezzo pieno: {euro.format(quote.annualFullPrice)} + IVA</span>
              )}
              {!enterpriseSelected && quote.annualUpfrontTotal !== null && quote.annualSavings !== null && (
                <span>Anticipato: {euro.format(quote.annualUpfrontTotal)} una tantum · −10% · risparmio {euro.format(quote.annualSavings)}</span>
              )}
              {currentCommission !== null && (
                <span>Provvigione simulata: {euro.format(billingCycle === 'annual' ? currentCommission.annualCommission : currentCommission.monthlyCommissionEquivalent)}{billingCycle === 'annual' ? '/anno anticipato' : '/mese'} · {percent.format(currentCommission.commissionRate)} effettivo</span>
              )}
            </div>
          </section>

          <section aria-labelledby="te-pricing-packages-title">
            <div className="te-section-heading te-pricing-package-heading">
              <h2 id="te-pricing-packages-title">Confronta i pacchetti</h2>
              <p>Ogni scheda mostra sia ciò che è incluso sia ciò che rimane fuori.</p>
            </div>
            <div className="te-pricing-package-grid">
              {COMMERCIAL_PACKAGES.filter((item) => item.id !== 'enterprise').map(renderPackageCard)}
            </div>
            <div className="te-pricing-enterprise-divider"><span>Soluzioni su misura</span></div>
            <div className="te-pricing-enterprise-wrap">
              {COMMERCIAL_PACKAGES.filter((item) => item.id === 'enterprise').map(renderPackageCard)}
            </div>
          </section>

          <section className="te-section-card te-pricing-billing" aria-labelledby="te-pricing-billing-title">
            <div>
              <h2 id="te-pricing-billing-title" className="te-section-title">Pagamento</h2>
              <p className="te-section-help">
                {enterpriseSelected || quote.monthlyTotal === null || quote.annualFullPrice === null || quote.annualUpfrontTotal === null
                  ? 'Canone da definire sulle necessità del cliente. Il pagamento annuale anticipato dà diritto al 10% di sconto sul canone concordato.'
                  : `${euro.format(quote.monthlyTotal)}/mese equivale a ${euro.format(quote.annualFullPrice)}/anno a prezzo pieno. Pagamento anticipato: ${euro.format(quote.annualUpfrontTotal)} una tantum (−10%).`}
              </p>
            </div>
            <IonSegment
              value={billingCycle}
              onIonChange={(event) => setBillingCycle(event.detail.value as CommercialBillingCycle)}
              aria-label="Modalità di pagamento"
            >
              <IonSegmentButton value="monthly">Mensile</IonSegmentButton>
              <IonSegmentButton value="annual">Annuale −10%</IonSegmentButton>
            </IonSegment>
          </section>

          <section className="te-pricing-columns">
            <div>
              <div className="te-section-heading"><h2>Volumi</h2></div>
              <IonList inset className="te-pricing-volume-list">
                <IonItem>
                  <IonInput
                    id="te-pricing-devices"
                    type="number"
                    min={1}
                    max={21}
                    inputmode="numeric"
                    label="Dispositivi attivi"
                    labelPlacement="stacked"
                    value={devices}
                    helperText="1–3 inclusi · 4–5 +10 € · 6–10 +25 € · 11–20 +50 € · oltre 20 preventivo"
                    onIonInput={(event) => setDevices(toBoundedInteger(event.detail.value, 1, 21))}
                  />
                </IonItem>
                <IonItem>
                  <IonInput
                    id="te-pricing-documents"
                    type="number"
                    min={0}
                    max={20}
                    inputmode="numeric"
                    label="Pacchetti da 100 analisi documenti extra"
                    labelPlacement="stacked"
                    value={extraDocumentPacks}
                    helperText="+10 € al mese per pacchetto"
                    onIonInput={(event) => setExtraDocumentPacks(toBoundedInteger(event.detail.value, 0, 20))}
                  />
                </IonItem>
                <IonItem lines="none">
                  <IonInput
                    id="te-pricing-probes"
                    type="number"
                    min={0}
                    max={50}
                    inputmode="numeric"
                    label="Sonde connesse"
                    labelPlacement="stacked"
                    value={connectedProbes}
                    helperText="+10 € per sonda al mese · hardware e gateway separati"
                    onIonInput={(event) => setConnectedProbes(toBoundedInteger(event.detail.value, 0, 50))}
                  />
                </IonItem>
              </IonList>
            </div>

            <div>
              <div className="te-section-heading"><h2>Moduli aggiuntivi</h2></div>
              <IonList inset className="te-pricing-addon-list">
                {COMMERCIAL_ADDONS.map((addon, index) => {
                  const includedInPackage = includedAddonIds.has(addon.id);
                  const checked = effectiveAddonIds.has(addon.id);
                  return (
                    <IonItem key={addon.id} lines={index === COMMERCIAL_ADDONS.length - 1 ? 'none' : 'inset'}>
                      <IonCheckbox
                        slot="start"
                        aria-label={`${addon.name}, ${includedInPackage ? 'incluso' : `più ${addon.price} euro al mese`}`}
                        checked={checked}
                        disabled={includedInPackage}
                        onIonChange={(event) => toggleAddon(addon.id, event.detail.checked)}
                      />
                      <IonLabel>
                        <div className="te-pricing-addon__title">
                          <strong>{addon.name}</strong>
                          <span>{includedInPackage ? 'Incluso' : `+${euro.format(addon.price)}/mese`}</span>
                        </div>
                        <p>{addon.note}</p>
                      </IonLabel>
                      <IonNote slot="end" className={`te-pricing-status te-pricing-status--${addon.status}`}>
                        {addon.status === 'assistito' ? 'Assistito' : addon.status === 'pilota' ? 'Pilota' : 'Vendibile'}
                      </IonNote>
                    </IonItem>
                  );
                })}
              </IonList>
            </div>
          </section>

          <section className="te-section-card te-pricing-projection" aria-labelledby="te-pricing-projection-title">
            <div className="te-pricing-projection__heading">
              <div>
                <span className="te-pricing-eyebrow">Piano partner</span>
                <h2 id="te-pricing-projection-title">Prospetto guadagni consulente</h2>
                <p>Premia il valore della singola vendita. Il numero di clienti non cambia la percentuale.</p>
              </div>
              <div className="te-pricing-projection__rules">
                <strong>20%</strong><span>sui primi 130 €/mese</span>
                <strong>40%</strong><span>solo sulla quota oltre 130 €/mese, non su tutto il contratto</span>
              </div>
            </div>

            <div className="te-pricing-projection__controls">
              <div className="te-pricing-projection-packages" role="group" aria-label="Pacchetto del prospetto">
                {COMMERCIAL_PACKAGES.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    aria-pressed={projectionPackageId === item.id}
                    className={projectionPackageId === item.id ? 'is-active' : ''}
                    onClick={() => {
                      setProjectionPackageId(item.id);
                      setProjectionPriceInput(item.monthlyPrice === null ? '' : String(item.monthlyPrice));
                    }}
                  >
                    <span>{item.name}</span>
                    <strong>{item.monthlyPrice === null ? 'Da definire' : euro.format(item.monthlyPrice)}</strong>
                  </button>
                ))}
              </div>
              <div className="te-pricing-negotiated">
                <div className="te-pricing-negotiated__fields">
                  <div>
                    <label htmlFor="te-pricing-negotiated-price">Canone concordato per cliente · €/mese</label>
                    <input
                      id="te-pricing-negotiated-price"
                      type="text"
                      inputMode="decimal"
                      value={projectionPriceInput}
                      aria-describedby="te-pricing-negotiated-help te-pricing-negotiated-error"
                      aria-invalid={projectionMonthlyPrice === null}
                      onChange={(event) => setProjectionPriceInput(event.target.value)}
                    />
                    <p id="te-pricing-negotiated-help">Dopo gli sconti commerciali e inclusi i moduli ricorrenti.</p>
                    <p id="te-pricing-negotiated-error" className="te-pricing-input-error" role="status">
                      {projectionMonthlyPrice === null ? 'Inserisci un importo da 0,01 a 100.000 €, con massimo due decimali e senza separatori delle migliaia.' : ''}
                    </p>
                  </div>
                  <div>
                    <label htmlFor="te-pricing-client-count">Numero clienti · solo moltiplicatore</label>
                    <input
                      id="te-pricing-client-count"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="100000"
                      step="1"
                      value={projectionClientsInput}
                      aria-describedby="te-pricing-client-count-help te-pricing-client-count-error"
                      aria-invalid={projectionClients === null}
                      onChange={(event) => setProjectionClientsInput(event.target.value)}
                    />
                    <p id="te-pricing-client-count-help">Moltiplica gli importi: non modifica mai percentuale o formula.</p>
                    <p id="te-pricing-client-count-error" className="te-pricing-input-error" role="status">
                      {projectionClients === null ? 'Inserisci un numero intero di clienti da 1 a 100.000.' : ''}
                    </p>
                  </div>
                </div>
                <IonButton
                  fill="outline"
                  disabled={quote.monthlyTotal === null}
                  onClick={() => {
                    if (quote.monthlyTotal === null) return;
                    setProjectionPackageId(packageId);
                    setProjectionPriceInput(String(quote.monthlyTotal));
                    setProjectionBillingCycle(billingCycle);
                  }}
                >
                  Usa offerta configurata
                </IonButton>
              </div>
            </div>

            {projectionCommission !== null && singleMonthly !== null && singleAnnual !== null && (
              <div className="te-pricing-commission" aria-live="polite" aria-label="Provvigione per singolo cliente">
                <h3>Come si compone la tua provvigione</h3>
                <div className="te-pricing-commission__parts">
                  <div><span>Quota base · {euro.format(projectionCommission.baseValue)} × 20%</span><strong>{euro.format(projectionCommission.baseCommission)}</strong></div>
                  <div><span>Quota eccedente · {euro.format(projectionCommission.upsellValue)} × 40%</span><strong>{euro.format(projectionCommission.upsellCommission)}</strong></div>
                  <div className="te-pricing-commission__total"><span>Totale mensile · {percent.format(projectionCommission.effectiveRate)} effettivo</span><strong>{euro.format(projectionCommission.monthlyCommission)}</strong></div>
                </div>
                <div className="te-pricing-commission__annual">
                  <div><span>Con 12 pagamenti mensili</span><strong>{euro.format(singleMonthly.annualCommission)}/anno</strong></div>
                  <div><span>Con pagamento annuale anticipato −10%</span><strong>{euro.format(singleAnnual.annualCommission)}/anno</strong></div>
                </div>
                <p>Un contratto da {euro.format(projectionCommission.monthlyContractValue)}/mese lascia a TrackEAT {euro.format(projectionCommission.monthlyContractValue - projectionCommission.monthlyCommission)}/mese prima dei costi di servizio: non è utile netto.</p>
              </div>
            )}

            <div className="te-pricing-projection__controls">
              <IonSegment
                value={projectionBillingCycle}
                onIonChange={(event) => setProjectionBillingCycle(event.detail.value as CommercialBillingCycle)}
                aria-label="Pagamento del prospetto"
              >
                <IonSegmentButton value="monthly">Mensile</IonSegmentButton>
                <IonSegmentButton value="annual">Annuale −10%</IonSegmentButton>
              </IonSegment>
            </div>

            {projectionScenario !== null && projectionMonthlyPrice !== null && <div className="te-pricing-table-wrap">
              <table className="te-pricing-table">
                <caption>
                  {projectionPackage.name}{projectionPackage.priceVisibility === 'quote' ? ' · canone personalizzato' : ''} · {projectionScenario.clients} clienti usati soltanto come moltiplicatore · scenario {euro.format(projectionMonthlyPrice)}/mese per cliente · annuo senza sconto anticipato {euro.format(annualFullPriceFromMonthly(projectionMonthlyPrice))} · {projectionBillingCycle === 'annual'
                    ? `incasso anticipato ${euro.format(annualUpfrontPriceFromMonthly(projectionMonthlyPrice))} per cliente (−10%). Le colonne mensili sono equivalenti medi, non rate da incassare.`
                    : 'fatturazione mensile senza sconto'}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Clienti</th>
                    <th scope="col">% effettiva</th>
                    <th scope="col">Fatturato / mese</th>
                    <th scope="col">Consulente / mese</th>
                    <th scope="col">Fatturato / anno</th>
                    <th scope="col">Consulente / anno</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row" data-label="Clienti">{projectionScenario.clients}</th>
                    <td data-label="% effettiva"><strong>{percent.format(projectionScenario.commissionRate)}</strong></td>
                    <td data-label="Fatturato / mese">{euro.format(projectionScenario.monthlyRevenueEquivalent)}</td>
                    <td data-label="Consulente / mese"><strong>{euro.format(projectionScenario.monthlyCommissionEquivalent)}</strong></td>
                    <td data-label="Fatturato / anno">{euro.format(projectionScenario.annualRevenue)}</td>
                    <td data-label="Consulente / anno"><strong>{euro.format(projectionScenario.annualCommission)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>}
            <p className="te-pricing-projection__note">
              Non esistono soglie o bonus quantità: il numero inserito moltiplica soltanto gli importi. Lo sconto annuale anticipato riduce del 10% anche la provvigione, dopo il calcolo delle due quote. Importi arrotondati ai centesimi per contratto. Il prospetto assume clienti allo stesso canone per 12 mesi interamente incassati, non stima il tasso di conversione.
            </p>
          </section>

          <section className="te-section-card te-pricing-notes" aria-labelledby="te-pricing-notes-title">
            <div className="te-section-card__header">
              <IonIcon icon={informationCircleOutline} aria-hidden="true" />
              <div>
                <h2 id="te-pricing-notes-title" className="te-section-title">Fuori dal calcolo automatico</h2>
                <p className="te-section-help">Voci da quotare o confermare separatamente</p>
              </div>
            </div>
            <div className="te-section-card__body">
              <ul>
                <li>Hardware, gateway, formazione iniziale e provider SDI restano separati.</li>
                <li>Enterprise non ha un prezzo fisso: canone, setup e perimetro sono definiti sulle necessità del cliente.</li>
                <li>Provvigioni sui canoni ricorrenti effettivamente incassati, IVA esclusa; durata e rinnovi da definire nell'accordo consulente.</li>
                <li>Hardware, setup, costi di terzi e sviluppo personalizzato non generano provvigioni automatiche. Il canone annuale anticipato, invece, è incluso.</li>
                <li>Enterprise non comprende sviluppo illimitato: perimetro e costi delle personalizzazioni vanno concordati a parte.</li>
              </ul>
            </div>
          </section>
        </main>
      </IonContent>

      <IonToast
        isOpen={toast !== null}
        message={toast ?? ''}
        duration={1800}
        onDidDismiss={() => setToast(null)}
      />
    </IonPage>
  );
}
